/**
 * CuratedTileRow lays out a curated room's games for the owner and lets them
 * reorder the games by dragging, with a mouse or with a long press on touch.
 * Dropping a game reports its new position; the order itself lives in the
 * view model.
 */
import * as React from 'react'
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '@merels/ui'

export interface CuratedTileRowItem {
  gameId: string
  displayName: string
}

export interface CuratedTileRowProps<T extends CuratedTileRowItem> {
  items: T[]
  /** Renders the tile for the game at a position in the row. */
  renderTile: (item: T, index: number) => React.ReactNode
  onMove: (gameId: string, toIndex: number) => void
  className?: string
}

function SortableTile({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, listeners, transform, transition, isDragging } = useSortable({ id })
  return (
    <div
      ref={setNodeRef}
      // Only the pointer listeners: the tile itself stays the focusable control.
      {...listeners}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn('w-full max-w-[200px] select-none', isDragging && 'relative z-20 opacity-80')}
      data-dragging={isDragging ? 'true' : undefined}
    >
      {children}
    </div>
  )
}

export function CuratedTileRow<T extends CuratedTileRowItem>({ items, renderTile, onMove, className }: CuratedTileRowProps<T>) {
  // A small movement threshold keeps a click a click; touch needs a long
  // press so the list still scrolls under a finger.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  )
  const nameOf = (id: string | number) => items.find((item) => item.gameId === id)?.displayName ?? String(id)
  const positionOf = (id: string | number) => items.findIndex((item) => item.gameId === id) + 1
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${nameOf(active.id)}.`,
    onDragOver: ({ active, over }) => (over ? `${nameOf(active.id)} is over position ${positionOf(over.id)} of ${items.length}.` : undefined),
    onDragEnd: ({ active, over }) => (over ? `${nameOf(active.id)} moved to position ${positionOf(over.id)} of ${items.length}.` : `${nameOf(active.id)} dropped.`),
    onDragCancel: ({ active }) => `Moving ${nameOf(active.id)} was cancelled.`,
  }

  // The pointer sensors swallow the click that ends a drag, so dropping a
  // game does not also open it.
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    onMove(String(active.id), items.findIndex((item) => item.gameId === over.id))
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      accessibility={{ announcements }}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map((item) => item.gameId)} strategy={rectSortingStrategy}>
        <div className={className}>
          {items.map((item, index) => (
            <SortableTile key={item.gameId} id={item.gameId}>
              {renderTile(item, index)}
            </SortableTile>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}
